<script setup>
import {onBeforeMount, reactive, ref} from 'vue'
import {Feedback, State, clone, getModel, getRoutes} from '@/paks/vu-app'
import {getView, getViewChildren, removeView, updateView} from './DashUtils.js'
import {icons} from '@/paks/vu-theme'

const props = defineProps({
    view: Object,
})

const emit = defineEmits(['input'])

const ReservedPages = [
    '/login',
    '/logout',
    '/claim',
    '/designer',
]

const page = reactive({
    components: [],
    deleting: false,
    existing: false,
    icons: [],
    roles: [],
    saving: false,
    view: {widgets: []},
})

const ReservedComponents = ['VuManager', 'VuSign']

//  Component refs
const self = ref(null)
const confirm = ref(null)

onBeforeMount(() => {
    if (props.view) {
        page.view = clone(props.view)
    }
    let view = page.view
    page.existing = view.path ? true : false
    if (view.properties) {
        view.properties = JSON.stringify(view.properties, null, 4)
    } else {
        view.properties = '{}'
    }
    if (!view.role) {
        view.role = 'inherited'
    }
    if (view.component == 'Page' && !view.board) {
        view.board = {
            css: [],
            type: 'page',
            widgets: [],
            widgetCss: [],
        }
    }
    let components = Object.keys(getRoutes().getComponents()).sort()
    page.components = components.filter((c) => !ReservedComponents.includes(c))
    page.roles = ['inherited', 'public', 'user', 'admin', 'owner']
    page.icons = Object.keys(icons).map((i) => `$${i}`).sort()
})

async function preSave() {
    let view = page.view
    if (view.name == 'New View') {
        throw new Error('Must set page name')
    }
    if (!page.existing) {
        let found = getView(view.path)
        if (found.key >= 0) {
            throw new Error('View path already in use')
        }
    }
    return true
}

async function save() {
    let view = clone(page.view)
    if (view.role == 'inherited') {
        view.role = null
    }
    if (view.properties && view.properties != '{}') {
        view.properties = JSON.parse(view.properties)
    }
    if (view.component == 'Page' && !view.board) {
        view.board = {
            css: [],
            name: view.title,
            layout: 'grid',
            framed: false,
            toolbar: false,
            type: 'page',
            widgets: [],
            widgetCss: [],
        }
    }
    await updateView(view)
    emit('input')
}

async function deleteView() {
    let view = page.view
    if (ReservedPages.includes(view.path)) {
        Feedback.error('This page is required by the framework and cannot be deleted')
        return
    }
    let msg = `Do you want to delete the page "${view.path || view.title}".`
    let count = getViewChildren(view.views).length
    if (count > 0) {
        msg += `\n\nThis page has ${count} sub-pages. If you delete this page, all of those sub-pages will also be deleted.`
    }
    if (!(await confirm.value.ask(msg))) {
        return
    }
    page.deleting = true
    await removeView(view)
    Feedback.info('View Deleted')
    page.deleting = false
    emit('input')
}
</script>

<template>
    <vu-form
        class="view-edit"
        help="/doc/ui/view/view-edit.html"
        ref="self"
        :data="page"
        :pre-save="preSave"
        :save="save"
        :title="`${page.existing ? 'Modify' : 'Add'} Page`">
        <vu-input
            v-model="page.view.path"
            label="Page URL"
            name="path"
            placeholder="Enter full URL Path"
            type="text"
            class="mb-2" />

        <vu-input
            v-model="page.view.title"
            label="Page Title"
            name="title"
            placeholder="Enter title"
            type="text"
            class="mb-2" />

        <vu-input type="select" v-model="page.view.component" label="Component" :items="page.components" />

        <vu-input
            v-if="page.view.component"
            label="Component Properties"
            v-model="page.view.properties"
            cols="6"
            placeholder="JSON Component Properties"
            type="textarea" />

        <vu-input
            v-model="page.view.menu"
            label="Sidebar Menu"
            name="menu"
            placeholder="Menu Text"
            type="text"
            class="mb-2" />

        <v-combobox
            v-if="page.view.menu"
            class="vu-input vcol vcol-12"
            v-model="page.view.icon"
            :items="page.icons"
            bg-color=""
            :color="undefined"
            density="compact"
            variant="underlined"
            label="Menu Icon"
            placeholder="Enter icon name">
            <template v-slot:item="{item, props}">
                <v-list-item v-bind="props">
                    <template v-slot:prepend>
                        <v-icon size="small" color="info" :icon="item.raw"></v-icon>
                    </template>
                </v-list-item>
            </template>
            <template v-slot:selection="{item}">
                <div>
                    <v-icon size="small" _color="info" :icon="item.title"></v-icon>
                    <span class="ml-2">{{ item.title }}</span>
                </div>
            </template>
        </v-combobox>

        <vu-input
            v-if="!page.view.component"
            label="Redirect"
            v-model="page.view.redirect"
            cols="6"
            type="select"
            :items="page.components" />

        <vu-input
            v-if="page.view.component"
            label="Role"
            v-model="page.view.role"
            cols="6"
            type="select"
            :items="page.roles" />

        <div class="vrow">
            <vu-input label="Tab Navigation" v-model="page.view.hasTabs" cols="4" type="switch" />
        </div>

        <div class="actions">
            <v-btn size="small" color="accent" type="submit" :loading="page.saving">Save</v-btn>
            <v-btn size="small" color="none" @click="emit('input', null)">Cancel</v-btn>
            <v-btn size="small" color="error" v-if="page.view.path" :loading="page.deleting" @click="deleteView">Delete</v-btn>
        </div>
        <vu-confirm ref="confirm" />
    </vu-form>
</template>

<style lang="scss">
.view-edit {
    .pick-view {
        margin: 16px 0 0px 0;
        width: 100%;
        .v-input__control {
            border-color: black;
            border-radius: 4px;
        }
    }
    .v-select-list {
        padding: 0;
        border: solid 1px rgb(var(--v-theme-border-lighten-1));
        border-radius: 0;
    }
    h3 {
        font-weight: normal;
        color: rgb(var(--v-theme-text));
    }
    .layouts {
        margin: 10px 0 20px 0;
    }
    .actions {
        margin: 20px 0 20px 0;
    }
    .live-data {
        //  When grid, helps alignment
        flex-grow: 30;
    }
    .v-list-item {
        min-height: 30px !important;
    }
    .v-list-item__content {
        padding: 6px 0 6px 0 !important;
    }
    .v-list {
        padding: 0;
    }
    .v-select__content {
        .v-list {
            background: rgb(var(--v-theme-background-lighten-1)) !important;
        }
    }
    margin-inline-start: 0 !important;
}
</style>

<style lang="scss" scoped>
.pick-items {
    font-size: 1rem;
}
.v-list-item {
    min-height: 30px !important;
}
.v-list-item__content {
    padding: 6px 0 6px 0 !important;
}
.v-list {
    padding: 0;
}
</style>
